import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import PreviewIcon from '@mui/icons-material/Preview';
import { SxProps, Theme } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import {
  CallOutcomeEnum,
  Customer,
  CustomerSchema,
  isNewAppointmentOutcome,
  isRequiredCustomerOutcome,
  isRequiredServiceTypeOutcome,
  isRequiredTradeOutcome,
  isStPortalRequiredAppointmentOutcome,
  isStPortalRequiredJobOutcome,
  Location,
  LocationSchema,
  Paths,
  RequireAtLeastOne,
  SERVICE_TYPES,
  ServiceTitanAppointment,
  ServiceTitanJob,
  ServiceTitanMappedCall,
  ServiceTitanPortalPhoneCallCreateDTO,
  ServiceTitanPortalPhoneCallCreateSchema,
  ServiceTypeEnum,
  SupportedProviderEnum,
  TradeEnum
} from '_lib/api/apiTypes.shim';
import { CompleteCallWrapper } from '_lib/callPop/common/CompleteCallWrapper/CompleteCallWrapper';
import { useCallLogNavigate } from '_lib/callPop/useCallLogNavigate';
import { Accordion } from '_lib/components/Accordion/Accordion';
import { AccordionDetails } from '_lib/components/AccordionDetail/AccordionDetail';
import { AccordionSummary } from '_lib/components/AccordionSummary/AccordionSummary';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Checkbox } from '_lib/components/Checkbox/Checkbox';
import { ErrorBoundaryFallback } from '_lib/components/ErrorBoundaryFallback/ErrorBoundaryFallback';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FieldSet } from '_lib/components/FieldSet/FieldSet';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FormControlLabel } from '_lib/components/FormControlLabel/FormControlLabel';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { Paper } from '_lib/components/Paper/Paper';
import { DateTimePicker } from '_lib/components/Pickers/DateTimePicker/DateTimePicker';
import { ShowMoreText } from '_lib/components/ShowMoreText/ShowMoreText';
import { Stack } from '_lib/components/Stack/Stack';

import { Typography } from '_lib/components/Typography/Typography';
import { AppointmentList } from '_lib/featureComponents/Appointment/AppointmentList';
import { AppointmentSelectClearable } from '_lib/featureComponents/Appointment/AppointmentSelectClearable';
import { CallOutcomeSelect } from '_lib/featureComponents/CallOutcomeSelect/CallOutcomeSelect';
import { CustomerInformation } from '_lib/featureComponents/Customer/CustomerInformation';
import { CustomerNameSearchSelect } from '_lib/featureComponents/Customer/CustomerNameSearchSelect';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { JobNumberSearchSelect } from '_lib/featureComponents/JobNumberSearchSelect/JobNumberSearchSelect';
import { LocationSelectClearable } from '_lib/featureComponents/Location/LocationSelectClearable';
import { NoteList } from '_lib/featureComponents/NoteList/NoteList';
import { ServiceTypeSelect } from '_lib/featureComponents/ServiceType/ServiceTypeSelect';
import { TradeSelect } from '_lib/featureComponents/Trades/TradeSelect';
import { useAlertDialog } from '_lib/hooks/AlertDialogHook';
import { isAfterHoursReviewContext, useAppointmentAfterHoursDialog } from '_lib/hooks/AppointmentAfterHoursDialogHook';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { useCustomerFormDialog } from '_lib/hooks/CustomerFormDialogHook';
import { useLocationFormDialog } from '_lib/hooks/LocationFormDialogHook';

import { useAuthRoles } from '_lib/hooks/useAuthRoles';
import { useAppDispatch, useAppSelector } from '_lib/redux/hooks';
import { addAppListener } from '_lib/redux/listenerMiddleware';
import { isAppointmentAfterHours } from '_lib/services/appointmentService';
import { roundedAccordion, ContrastIconButton } from '_lib/styles/PhoneCall.styled';
import { PhoneCallURLState } from '_lib/types/PhoneCall.types';
import { DateFormatEnum, DateUtils } from '_lib/utils/date.utils';
import { getAppointmentNumberAndStatus, getFullName, sleep, toSxArray } from '_lib/utils/helpers';
import { logDebug } from '_lib/utils/logging';
import { reportException } from '_lib/utils/sentryService';

import { useOurSnackbar } from '_lib/utils/snackUtils';

import { ClearIssueItem } from '_lib/utils/validationErrorUtils';

import { zipZoneDialogCheck } from '_lib/utils/zipZoneDialogUtils';

import {
  isNewZoneSelectedPredicate,
  selectZoneZipContext,
  StPortalV2CompanyContext
} from '../../../../common/CompanyPane/Company.slice';

import { useMissingJobInfoDialog } from '../../hooks/MissingJobInfoDialogHook';
import {
  initialize,
  PhoneCallContext,
  selectPhoneCallContext,
  selectPhoneCallFieldIssues,
  selectPhoneCallNonFieldErrors,
  setPartialPhoneCallContext,
  setPhoneCallFieldIssues,
  setPhoneCallNonFieldErrors
} from '../../StPortalPhoneCall.slice';

interface CallDetailsBoxProps {
  companyContext: StPortalV2CompanyContext;
  phoneCallURLState: PhoneCallURLState;
  mappedCall: ServiceTitanMappedCall | null;
  refForDialog: React.RefObject<null>;
  aiCallId?: string;
  sx?: SxProps<Theme>;
}

// TODO: rethink this!
export const handledIssues: Pick<
  { [k in Paths<ServiceTitanPortalPhoneCallCreateDTO>]: k },
  | 'outcome'
  | 'internalNotes'
  | 'previousAppointmentDateTime'
  | 'trade'
  | 'serviceType'
  | 'providerData.providerJobId'
  | 'providerData.providerAppointmentId'
  | 'providerData.providerCustomerId'
  | 'providerData.providerLocationId'
> = {
  outcome: 'outcome',
  internalNotes: 'internalNotes',
  previousAppointmentDateTime: 'previousAppointmentDateTime',
  trade: 'trade',
  serviceType: 'serviceType',
  'providerData.providerJobId': 'providerData.providerJobId',
  'providerData.providerAppointmentId': 'providerData.providerAppointmentId',
  'providerData.providerCustomerId': 'providerData.providerCustomerId',
  'providerData.providerLocationId': 'providerData.providerLocationId'
} as const;

const SYNCED_FROM_ST_NOTE = 'Synced From Service Titan' as const;

function getAutoSelectAppointmentForJob(
  appointments: ServiceTitanAppointment[],
  outcome: CallOutcomeEnum | undefined,
  callStart: Date
): ServiceTitanAppointment | undefined {
  // if we only have one appointment for the job, select it:
  if (appointments.length === 1) return appointments[0];

  if (appointments.length > 1) {
    const now = new Date();
    const appointmentsModified = appointments.filter(
      (a) => DateUtils.isAfter(a.providerModifiedAt, callStart) && DateUtils.isBefore(a.providerModifiedAt, now)
    );

    // if we only have one appointment modified, then auto select it
    if (appointmentsModified.length === 1) return appointmentsModified[0];

    // if we haven't found an appointment to auto select and outcome is cancelled, auto select next upcoming appointment
    if (outcome === CallOutcomeEnum.Cancellation) {
      return appointments
        .filter((a) => DateUtils.isAfter(a.start, callStart))
        .sort((a, b) => (a.start < b.start ? -1 : 1))[0];
    }
  }
  return undefined;
}

export const CallDetailsBox = (props: CallDetailsBoxProps): React.ReactElement => {
  const { companyContext, phoneCallURLState, mappedCall, refForDialog, aiCallId, sx } = props;

  const api = useContext(ApiContext);
  const serviceTitanApi = useMemo(() => api.serviceTitan(companyContext.company.id), [api, companyContext.company.id]);

  const { navigateToCallLog } = useCallLogNavigate();
  const { enqueueSnackbar } = useOurSnackbar();
  const authRoles = useAuthRoles();

  const alertDialog = useAlertDialog();
  const confirmationDialog = useConfirmationDialog();
  const customerFormDialog = useCustomerFormDialog();
  const locationFormDialog = useLocationFormDialog();
  const missingJobInfoDialog = useMissingJobInfoDialog();
  const appointmentAfterHoursDialog = useAppointmentAfterHoursDialog();

  const dispatch = useAppDispatch();

  const phoneCallContext = useAppSelector(selectPhoneCallContext);
  const fieldIssues = useAppSelector(selectPhoneCallFieldIssues);
  const nonFieldErrors = useAppSelector(selectPhoneCallNonFieldErrors);

  const zoneZipContext = useAppSelector(selectZoneZipContext);

  const [showCompleteCall, setShowCompleteCall] = useState(false);
  const [isSavingOrLoading, setIsSavingOrLoading] = useState(false);

  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [isCompleteOrReviewEnabled, setIsCompleteOrReviewEnabled] = useState(true);

  const handlePhoneCallContextUpdate = useCallback(
    (updates: RequireAtLeastOne<Partial<PhoneCallContext>>, clearIssues?: ClearIssueItem[]) => {
      dispatch(setPartialPhoneCallContext({ updates, clearIssues }));
    },
    [dispatch]
  );

  useEffect(() => {
    // - New Zone Selected Handler ST Portal V2 - //
    return dispatch(
      addAppListener({
        predicate: isNewZoneSelectedPredicate,
        effect: () => {
          logDebug('// - New Zone Selected Handler ST Portal V2 - //');
          if (nonFieldErrors?.length) {
            const updatedErrors = nonFieldErrors.filter((msg) => !msg.toLowerCase().includes('zone'));
            if (updatedErrors.length !== nonFieldErrors.length) dispatch(setPhoneCallNonFieldErrors(updatedErrors));
          }
        }
      })
    );
  }, [dispatch, nonFieldErrors]);

  const toValidCustomer = async (customer: Customer) => {
    let validCustomer: Customer | null = null;
    const customerResult = CustomerSchema.safeParse(customer);
    if (!customerResult.success) {
      const result = await customerFormDialog.show({
        customer,
        customerResource: serviceTitanApi.customers
      });
      if (!result || result.action === 'CANCEL') validCustomer = null;
      else {
        validCustomer = result.value.customer;
      }
    } else {
      validCustomer = customer;
    }

    return validCustomer;
  };

  const toValidLocation = async (location: Location, locationsForCustomer: Location[]) => {
    let validLocation: Location | null = null;
    const locationResult = LocationSchema.safeParse(location);
    if (!locationResult.success) {
      const result = await locationFormDialog.show({
        location,
        locationResource: serviceTitanApi.locations
      });
      if (!result || result.action === 'CANCEL') validLocation = null;
      else {
        validLocation = result.value;
        const updatedLocations = locationsForCustomer.map((l) =>
          l.providerId !== location.providerId ? l : result.value
        );
        handlePhoneCallContextUpdate({ locationsForCustomer: updatedLocations });
      }
    } else {
      validLocation = location;
    }

    return validLocation;
  };

  const handleSetCallOutcome = async (callOutcome: CallOutcomeEnum) => {
    // reset any error state based on setting outcome:
    const clearIssues: ClearIssueItem[] = [handledIssues.outcome];

    if (!isStPortalRequiredJobOutcome(callOutcome)) {
      clearIssues.push(handledIssues['providerData.providerJobId']);
    }

    if (!isStPortalRequiredAppointmentOutcome(callOutcome)) {
      clearIssues.push(handledIssues['providerData.providerAppointmentId']);
    }

    if (!isRequiredCustomerOutcome(callOutcome)) {
      clearIssues.push(
        handledIssues['providerData.providerCustomerId'],
        handledIssues['providerData.providerLocationId']
      );
    }

    handlePhoneCallContextUpdate({ callOutcome }, clearIssues);
  };

  const handleSetJob = async (job: ServiceTitanJob | null) => {
    if (job) {
      try {
        setIsSavingOrLoading(true);

        let customer = await serviceTitanApi.customers.getById({ id: job.providerCustomerId });
        const isCustomerValid = CustomerSchema.safeParse(customer).success;

        let location = await serviceTitanApi.locations.getById({ id: job.providerLocationId });
        const isLocationValid = LocationSchema.safeParse(location).success;

        if (!isCustomerValid || !isLocationValid) {
          const result = await missingJobInfoDialog.show({
            job,
            company: companyContext.company,
            customer,
            customerResource: serviceTitanApi.customers,
            location,
            locationResource: serviceTitanApi.locations
          });

          if (!result || result.action === 'CANCEL') {
            setIsSavingOrLoading(false);
            return false;
          } else {
            customer = { ...result.value.customer, notes: customer.notes, contacts: customer.contacts };
            location = result.value.location;
          }
        }

        handlePhoneCallContextUpdate({ job, customer, location }, [
          handledIssues['providerData.providerJobId'],
          handledIssues['providerData.providerCustomerId'],
          handledIssues['providerData.providerLocationId']
        ]);

        const { results } = await serviceTitanApi.appointments.unpagedList({
          params: { providerJobId: job.providerId }
        });
        const appointmentsForJob = results.sort((a, b) => (a.start < b.start ? -1 : 1));
        handlePhoneCallContextUpdate({ appointmentsForJob });

        const autoSelectedAppointment = getAutoSelectAppointmentForJob(
          appointmentsForJob,
          phoneCallContext.callOutcome,
          new Date(phoneCallURLState.durationStart)
        );

        if (autoSelectedAppointment) {
          handlePhoneCallContextUpdate({ appointment: autoSelectedAppointment }, [
            handledIssues['providerData.providerAppointmentId']
          ]);
        }
        setIsSavingOrLoading(false);
      } catch (error) {
        reportException(error);
        enqueueSnackbar('Error Fetching Job Info');

        setIsSavingOrLoading(false);
        handlePhoneCallContextUpdate({
          job: null,
          appointment: null,
          customer: null,
          location: null,
          appointmentsForJob: [],
          locationsForCustomer: []
        });
        return false;
      }
    } else {
      handlePhoneCallContextUpdate({
        job,
        appointment: null,
        customer: null,
        location: null,
        appointmentsForJob: [],
        locationsForCustomer: []
      });
    }

    return true;
  };

  const handleSetAppointment = async (appointment: ServiceTitanAppointment | null) => {
    handlePhoneCallContextUpdate(
      { appointment },
      appointment ? [handledIssues['providerData.providerAppointmentId']] : undefined
    );
  };

  const handleSetTrade = (value: TradeEnum) => {
    handlePhoneCallContextUpdate({ trade: value }, [handledIssues.trade]);
  };

  const handleSetServiceType = (value: ServiceTypeEnum) => {
    handlePhoneCallContextUpdate({ serviceType: value }, [handledIssues.serviceType]);
  };

  const handleSetCustomer = async (customer: Customer | null) => {
    if (customer) {
      try {
        const validCustomer = await toValidCustomer(customer);

        handlePhoneCallContextUpdate(
          { customer: validCustomer },
          validCustomer ? [handledIssues['providerData.providerCustomerId']] : undefined
        );
        if (validCustomer === null) return false;

        setIsSavingOrLoading(true);
        const serviceTitanLocationsRes = await serviceTitanApi.locations.allCustomerLocations({
          params: { providerCustomerId: customer.providerId }
        });
        handlePhoneCallContextUpdate({ locationsForCustomer: serviceTitanLocationsRes.results });

        const singleLocation = serviceTitanLocationsRes.results.length === 1 && serviceTitanLocationsRes.results[0];
        if (singleLocation) {
          const validLocation = await toValidLocation(singleLocation, serviceTitanLocationsRes.results);
          handlePhoneCallContextUpdate(
            { location: validLocation },
            validLocation ? [handledIssues['providerData.providerLocationId']] : undefined
          );
        }
      } catch (error) {
        reportException(error);
        enqueueSnackbar('Error Fetching Customer Locations');
      } finally {
        setIsSavingOrLoading(false);
      }
    } else {
      handlePhoneCallContextUpdate({ customer, location: null, locationsForCustomer: [] });
    }
    return true;
  };

  const handleSetLocation = async (location: Location | null) => {
    if (location) {
      const validLocation = await toValidLocation(location, phoneCallContext.locationsForCustomer);
      handlePhoneCallContextUpdate(
        { location: validLocation },
        validLocation ? [handledIssues['providerData.providerLocationId']] : undefined
      );
    } else {
      handlePhoneCallContextUpdate({ location: null });
    }
  };

  const reviewCallAndSyncContext = async (data: ServiceTitanPortalPhoneCallCreateDTO) => {
    const res = await api.serviceTitanPortalPhoneCallsV2.reviewCall(data);

    const updatedAppointmentsForJob = phoneCallContext.appointmentsForJob.map((appointment) => {
      const matchingUpdatedAppointment = res.stOtherAppointments?.find(
        (updatedAppointment) => updatedAppointment.providerId === appointment.providerId
      );
      if (matchingUpdatedAppointment) return matchingUpdatedAppointment;
      // NOTE - list of updated appointments does NOT include the selected appointment
      if (res.stAppointment?.providerId === appointment.providerId) return res.stAppointment;
      return appointment;
    });

    const phoneCallContextUpdates = {
      job: res.stJob,
      appointmentsForJob: updatedAppointmentsForJob,
      appointment: res.stAppointment,
      customer: res.stCustomer,
      location: res.stLocation,
      jobCancelEvent: res.stJobCancelEvent,
      jobHistoryNotes: res.stJobHistoryNotes || [],
      otherAppointments: res.stOtherAppointments || [],
      assignedTechnician: res.stTechnician || null
    };

    handlePhoneCallContextUpdate(phoneCallContextUpdates);

    return { ...phoneCallContext, ...phoneCallContextUpdates };
  };

  const completeOrReviewCall = async (operation: 'review' | 'complete', data: ServiceTitanPortalPhoneCallCreateDTO) => {
    try {
      setIsSavingOrLoading(true);

      if (operation === 'review') {
        const updatedPhoneCallContext = await reviewCallAndSyncContext(data);

        if (
          isNewAppointmentOutcome(updatedPhoneCallContext.callOutcome) &&
          isAfterHoursReviewContext(companyContext) &&
          updatedPhoneCallContext.appointment &&
          isAppointmentAfterHours({
            appointmentStart: updatedPhoneCallContext.appointment.start,
            timezone: companyContext.company.timezone
          })
        ) {
          setIsSavingOrLoading(false);
          const afterHoursResult = await appointmentAfterHoursDialog.show({
            container: refForDialog.current,
            companyContext,
            zoneZipContext,
            callOutcome: phoneCallContext.callOutcome,
            appointmentPartial: phoneCallContext.appointment,
            jobNo: phoneCallContext.job?.jobNo,
            assignedTech: phoneCallContext.assignedTechnician,
            disableEnforceFocus: true,
            dismissable: false
          });

          if (afterHoursResult?.action !== 'CONFIRM') return;
          // NOTE - need to sync data that was changed in ST while the dialog was open
          setIsSavingOrLoading(true);
          await reviewCallAndSyncContext(data);
          setIsSavingOrLoading(false);
        }

        setShowCompleteCall(true);
      } else {
        await api.serviceTitanPortalPhoneCallsV2.create(data);
        navigateToCallLog();
      }
    } catch (err) {
      // If we somehow pass validation client side and fail on the api, just dump it (for now)
      if (err instanceof BadRequestError) {
        dispatch(setPhoneCallNonFieldErrors(err.responseData.nonFieldErrors));
        dispatch(
          setPhoneCallFieldIssues(
            err.responseData.fieldIssues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors') || []
          )
        );
      }
      setShowCompleteCall(false);
      reportException(err);
    } finally {
      setIsSavingOrLoading(false);
    }
  };

  const handleCallButtonClick = async (operation: 'review' | 'complete') => {
    dispatch(setPhoneCallFieldIssues([]));
    dispatch(setPhoneCallNonFieldErrors([]));

    if (!phoneCallContext.callOutcome) {
      enqueueSnackbar('Call Outcome is Required');
      return;
    }

    const isReschedule = phoneCallContext.callOutcome === CallOutcomeEnum.Reschedule;

    const providerMappedCall =
      companyContext.company.dataProvider.providerOptions.tenantId ===
      companyContext.companyProviderData?.originalCompanyTenantId
        ? mappedCall
        : null;

    const result = ServiceTitanPortalPhoneCallCreateSchema.safeParse({
      id: phoneCallURLState.phoneCallId,
      providerType: SupportedProviderEnum.SERVICE_TITAN,
      companyId: companyContext.company.id,
      callerId: phoneCallURLState.callerId,
      companyDNIS: phoneCallURLState.companyDNIS,
      companyIsZoned: companyContext.company.isZoned,
      companySwitched: companyContext.switched,
      duration: Date.now() - phoneCallURLState.durationStart,
      isManualCall: phoneCallURLState.isManualCall,
      outcome: phoneCallContext.callOutcome,
      previousAppointmentDateTime: isReschedule ? phoneCallContext.previousAppointmentDateTime : null,
      sendEscalationEmail: phoneCallContext.sendEscalationEmail,
      companyZoneId: zoneZipContext?.selectedZone?.id || null,
      companyZoneTitle: zoneZipContext?.selectedZone?.title || null,
      internalNotes: phoneCallContext.internalNotes || null,
      originalCompany: companyContext.originalCompanyName || null,
      trade: phoneCallContext.trade || null,
      serviceType: phoneCallContext.serviceType || null,
      providerData: {
        providerMappedCall,
        providerJobId: phoneCallContext.job?.providerId || null,
        providerAppointmentId: phoneCallContext.appointment?.providerId || null,
        providerCustomerId: phoneCallContext.customer?.providerId || null,
        providerLocationId: phoneCallContext.location?.providerId || null
      },
      aiCallId: aiCallId || null,
      telephonyId: phoneCallURLState.telephonyId || null
    });

    if (result.success) {
      if (!companyContext.company.isZoned || operation !== 'complete') {
        return completeOrReviewCall(operation, result.data);
      }

      await zipZoneDialogCheck({
        companyContext,
        selectedZone: zoneZipContext?.selectedZone || null,
        zipOrPostal: phoneCallContext.location?.zipOrPostal,
        confirmationCallback: () => completeOrReviewCall(operation, result.data),
        confirmationDialog,
        alertDialog
      });
    } else if (!result.success) {
      dispatch(setPhoneCallFieldIssues(result.error.issues.filter((issue) => issue.path?.[0] !== 'nonFieldErrors')));

      // NOTE: this also ensures we display any custom path field fieldIssues, ie:
      //       for phone call, there are a few cases where a field issue may have path: ['nonFieldErrors']
      const handledIssueValues = Object.values(handledIssues);
      const extraErrors = result.error.issues
        .filter(({ path }) => !handledIssueValues.find((issue) => issue === path.join('.')))
        .map(({ message }) => message);

      if (extraErrors.length) dispatch(setPhoneCallNonFieldErrors(extraErrors));
      setShowCompleteCall(false);
      enqueueSnackbar('Phone Call is Invalid!');
    }
  };

  const reInitCallDetails = () => {
    dispatch(initialize({ company: companyContext.company, options: { keepExistingCallData: true } }));
    setShowCompleteCall(false);
  };

  return (
    <ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag('pane', 'Call Details Box');
      }}
      fallback={({ resetError }) => (
        <ErrorBoundaryFallback
          title='Error Displaying Call Details'
          onRefresh={() => {
            reInitCallDetails();
            resetError();
          }}
        />
      )}>
      <Paper
        variant='elevation'
        sx={[
          (theme) => ({
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxHeight: '50%',
            backgroundColor: theme.palette.callPane.main,
            color: theme.palette.callPane.contrastText,
            borderTopLeftRadius: aiCallId ? 0 : undefined,
            borderTopRightRadius: aiCallId ? 0 : undefined,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0
          }),
          ...toSxArray(sx)
        ]}>
        <BackdropSpinner open={isSavingOrLoading} variant='dark' />
        <Accordion
          disableGutters
          square
          expanded={isAccordionOpen}
          elevation={0}
          sx={{
            color: 'inherit',
            backgroundColor: 'inherit',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            '& .MuiAccordion-root.Mui-expanded': {
              marginTop: 0
            },
            '& .MuiCollapse-root': {
              overflow: 'auto'
            },
            ...roundedAccordion
          }}>
          <AccordionSummary
            component='span'
            expandIcon={
              isAccordionOpen && (
                <IconButton onClick={() => setIsAccordionOpen(false)} sx={{ pointerEvents: 'all' }}>
                  <CloseIcon sx={(theme) => ({ color: theme.palette.callPane.contrastText })} />
                </IconButton>
              )
            }
            sx={{ display: 'flex', width: '100%', pointerEvents: 'none', minHeight: '68.5px' }}>
            {!isAccordionOpen && (
              <Button
                onClick={async () => {
                  setIsAccordionOpen(true);
                  setIsCompleteOrReviewEnabled(false);
                  await sleep(500);
                  setIsCompleteOrReviewEnabled(true);
                }}
                fullWidth
                startIcon={<PhoneIcon />}
                color='success'
                sx={[{ pointerEvents: 'all' }]}
                variant='contained'>
                Call Details
              </Button>
            )}
            {isAccordionOpen && (
              <Stack
                direction='row'
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: 'center', marginRight: 2, flexGrow: 1 }}>
                <Typography variant='h4' text={showCompleteCall ? 'REVIEW CALL' : 'CALL DETAILS'} />
                {showCompleteCall && (
                  <ContrastIconButton
                    sx={{
                      padding: 0,
                      minHeight: 0,
                      minWidth: 0,
                      pointerEvents: 'all'
                    }}
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCompleteCall(false);
                    }}>
                    <ArrowBackIcon />
                  </ContrastIconButton>
                )}
              </Stack>
            )}
          </AccordionSummary>
          <AccordionDetails
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              paddingX: 2,
              paddingY: 0
            }}>
            {!!nonFieldErrors.length && <ErrorDisplay errors={nonFieldErrors} sx={{ borderRadius: 1, marginY: 1 }} />}
            {!showCompleteCall && (
              <Stack
                useFlexGap
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.default,
                  padding: 1,
                  borderRadius: 1,
                  overflow: 'auto'
                })}>
                <CallOutcomeSelect
                  callOutcome={phoneCallContext.callOutcome}
                  onSetCallOutcome={handleSetCallOutcome}
                  issuePath={handledIssues.outcome}
                  issues={fieldIssues}
                />
                <JobNumberSearchSelect
                  job={phoneCallContext.job}
                  onSetJob={handleSetJob}
                  companyId={companyContext.company.id}
                  currentProviderCustomerId={phoneCallContext.customer?.providerId}
                  required={isStPortalRequiredJobOutcome(phoneCallContext.callOutcome)}
                  issuePath={handledIssues['providerData.providerJobId']}
                  issues={fieldIssues}
                />
                {phoneCallContext.job && (
                  <AppointmentSelectClearable
                    onSetServiceTitanAppointment={handleSetAppointment}
                    serviceTitanAppointment={phoneCallContext.appointment}
                    serviceTitanAppointments={phoneCallContext.appointmentsForJob}
                    jobNo={phoneCallContext.job.jobNo}
                    required={isStPortalRequiredAppointmentOutcome(phoneCallContext.callOutcome)}
                    disabled={!phoneCallContext.appointmentsForJob.length}
                    timezone={companyContext.company.timezone}
                    issuePath={handledIssues['providerData.providerAppointmentId']}
                    issues={fieldIssues}
                  />
                )}
                {phoneCallContext.callOutcome === CallOutcomeEnum.Reschedule && (
                  <DateTimePicker
                    label='Previous Appointment Date'
                    onChange={(date) =>
                      handlePhoneCallContextUpdate({ previousAppointmentDateTime: date ? date.toISOString() : null }, [
                        handledIssues.previousAppointmentDateTime
                      ])
                    }
                    textFieldProps={{
                      required: true,
                      error: { issuePath: handledIssues.previousAppointmentDateTime, issues: fieldIssues }
                    }}
                    timeSteps={{ minutes: 30 }}
                    timezone={companyContext.company.timezone}
                    value={phoneCallContext.previousAppointmentDateTime}
                  />
                )}
                {isRequiredTradeOutcome(phoneCallContext.callOutcome) && (
                  <TradeSelect
                    trade={phoneCallContext.trade}
                    onSetTrade={handleSetTrade}
                    trades={companyContext.company.callCenter.trades}
                    issuePath={handledIssues.trade}
                    issues={fieldIssues}
                  />
                )}
                {isRequiredServiceTypeOutcome(phoneCallContext.callOutcome) && (
                  <ServiceTypeSelect
                    serviceType={phoneCallContext.serviceType}
                    onSetServiceType={handleSetServiceType}
                    serviceTypes={SERVICE_TYPES}
                    issuePath={handledIssues.serviceType}
                    issues={fieldIssues}
                  />
                )}
                {!isStPortalRequiredJobOutcome(phoneCallContext.callOutcome) && !phoneCallContext.job && (
                  <>
                    <CustomerNameSearchSelect
                      customer={phoneCallContext.customer}
                      onSetCustomer={handleSetCustomer}
                      companyId={companyContext.company.id}
                      required={isRequiredCustomerOutcome(phoneCallContext.callOutcome)}
                      issuePath={handledIssues['providerData.providerCustomerId']}
                      issues={fieldIssues}
                    />
                    {phoneCallContext.customer && (
                      <LocationSelectClearable
                        onSetLocation={handleSetLocation}
                        location={phoneCallContext.location}
                        locations={phoneCallContext.locationsForCustomer}
                        disabled={!phoneCallContext.locationsForCustomer.length}
                        required={isRequiredCustomerOutcome(phoneCallContext.callOutcome)}
                        issuePath={handledIssues['providerData.providerLocationId']}
                        issues={fieldIssues}
                      />
                    )}
                  </>
                )}
                {phoneCallContext.customer && phoneCallContext.location && phoneCallContext.job && (
                  <CustomerInformation customer={phoneCallContext.customer} location={phoneCallContext.location} />
                )}
                {!authRoles.isAccountUser && !authRoles.isAccountAdmin && (
                  <FlexBox>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color='primary'
                          checked={phoneCallContext.sendEscalationEmail}
                          onChange={(e) => {
                            if (!e.target.checked)
                              handlePhoneCallContextUpdate({ sendEscalationEmail: false }, [
                                handledIssues.internalNotes
                              ]);
                            else handlePhoneCallContextUpdate({ sendEscalationEmail: true });
                          }}
                        />
                      }
                      label={<Typography text='Send Escalation Email' />}
                    />
                  </FlexBox>
                )}
              </Stack>
            )}
            {showCompleteCall && (
              <Stack
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.default,
                  padding: 1,
                  borderRadius: 1,
                  overflow: 'auto'
                })}>
                <FieldSet legendText='Outcome'>
                  <Typography text={phoneCallContext?.callOutcome || 'No Outcome Selected'} />
                </FieldSet>

                {zoneZipContext?.selectedZone && (
                  <FieldSet legendText='Zone Info'>
                    <Typography text={zoneZipContext.selectedZone.title} />
                  </FieldSet>
                )}

                {phoneCallContext.job && (
                  <FieldSet legendText={`Job Info #${phoneCallContext.job.jobNo} - ${phoneCallContext.job.status}`}>
                    <Stack spacing={2}>
                      {phoneCallContext.job.notes && (
                        <HeaderBox headerText='Summary'>
                          <ShowMoreText text={phoneCallContext.job.notes} isRichText={true} />
                        </HeaderBox>
                      )}
                      {phoneCallContext.jobCancelEvent && (
                        <HeaderBox headerText='Cancel Info' infoIconText={SYNCED_FROM_ST_NOTE}>
                          <ShowMoreText
                            text={`(${phoneCallContext.jobCancelEvent.name}) ${phoneCallContext.jobCancelEvent.notes}`}
                          />
                        </HeaderBox>
                      )}
                      {phoneCallContext.jobHistoryNotes.length > 0 && (
                        <HeaderBox headerText='History Notes' infoIconText={SYNCED_FROM_ST_NOTE}>
                          <NoteList
                            notes={phoneCallContext.jobHistoryNotes}
                            timezone={companyContext.company.timezone}
                            listItemSx={{ paddingLeft: 0 }}
                            sx={{ paddingBottom: 0 }}
                          />
                        </HeaderBox>
                      )}
                      {!phoneCallContext.job.notes &&
                        !phoneCallContext.jobCancelEvent &&
                        !phoneCallContext.jobHistoryNotes.length && (
                          <Typography text='No job summary or notes exists' />
                        )}
                    </Stack>
                  </FieldSet>
                )}

                {phoneCallContext.job && phoneCallContext.appointment && (
                  <FieldSet
                    legendText={`Appointment Info ${getAppointmentNumberAndStatus(
                      phoneCallContext.appointment,
                      phoneCallContext.job.jobNo
                    )}`}>
                    <Stack>
                      <HeaderBox headerText='Date and Time'>
                        <Typography
                          text={DateUtils.format(phoneCallContext.appointment.start, DateFormatEnum.DateTimeWeekday, {
                            timezone: companyContext.company.timezone,
                            showTimezone: true
                          })}
                        />
                      </HeaderBox>
                      {phoneCallContext.appointment.specialInstructions && (
                        <HeaderBox headerText='Special Instructions' infoIconText={SYNCED_FROM_ST_NOTE}>
                          <ShowMoreText text={phoneCallContext.appointment.specialInstructions} isRichText={true} />
                        </HeaderBox>
                      )}
                      {phoneCallContext.assignedTechnician && (
                        <HeaderBox headerText='Assigned Technician' infoIconText={SYNCED_FROM_ST_NOTE}>
                          <Typography text={getFullName(phoneCallContext.assignedTechnician)} />
                        </HeaderBox>
                      )}
                    </Stack>
                  </FieldSet>
                )}

                {phoneCallContext.job && phoneCallContext.otherAppointments.length > 0 && (
                  <FieldSet legendText='Other Appointments' infoIconText={SYNCED_FROM_ST_NOTE}>
                    <AppointmentList
                      appointments={phoneCallContext.otherAppointments}
                      jobNo={phoneCallContext.job.jobNo}
                      timezone={companyContext.company.timezone}
                    />
                  </FieldSet>
                )}

                {isRequiredTradeOutcome(phoneCallContext.callOutcome) && phoneCallContext.trade && (
                  <FieldSet legendText='Trade'>
                    <Typography text={TradeEnum[phoneCallContext.trade]} />
                  </FieldSet>
                )}

                {isRequiredServiceTypeOutcome(phoneCallContext.callOutcome) && phoneCallContext.serviceType && (
                  <FieldSet legendText='ServiceType'>
                    <Typography text={ServiceTypeEnum[phoneCallContext.serviceType]} />
                  </FieldSet>
                )}

                {phoneCallContext.customer && (
                  <CustomerInformation customer={phoneCallContext.customer} location={phoneCallContext.location} />
                )}

                {phoneCallContext.sendEscalationEmail && (
                  <FieldSet legendText='Notifications'>
                    <Typography text='Escalation email will be sent' />
                  </FieldSet>
                )}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
        {isAccordionOpen && (
          <FlexBox sx={{ width: '100%', paddingX: 2, paddingY: 2, minHeight: '68.5px' }}>
            <CompleteCallWrapper nonFieldErrors={nonFieldErrors}>
              <Button
                onClick={() =>
                  // only process click event if we're NOT currently saving or running another request
                  (!isSavingOrLoading || !!isCompleteOrReviewEnabled) &&
                  handleCallButtonClick(showCompleteCall ? 'complete' : 'review')
                }
                fullWidth
                startIcon={showCompleteCall ? <CheckIcon /> : <PreviewIcon />}
                color='success'
                variant='contained'>
                {showCompleteCall ? `Complete Call` : `Review Call`}
              </Button>
            </CompleteCallWrapper>
          </FlexBox>
        )}
      </Paper>
      {alertDialog.element}
      {confirmationDialog.element}
      {customerFormDialog.element}
      {locationFormDialog.element}
      {missingJobInfoDialog.element}
      {appointmentAfterHoursDialog.element}
    </ErrorBoundary>
  );
};
